/**
 * Payout History Component
 * Display list of payout requests and their status
 */

import { Link } from 'react-router-dom';

interface Payout {
  id: number;
  amount: number;
  commissionCount: number;
  paymentMethod: string;
  status: string;
  requestedAt: string;
  completedAt: string | null;
  transactionId: string | null;
  failureReason: string | null;
}

interface PayoutHistoryProps {
  payouts: Payout[];
  onRefresh: () => void;
}

export default function PayoutHistory({ payouts, onRefresh }: PayoutHistoryProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-900/30 text-green-400 border-green-700';
      case 'pending':
        return 'bg-yellow-900/30 text-yellow-400 border-yellow-700';
      case 'processing':
        return 'bg-blue-900/30 text-orange-400 border-blue-700';
      case 'failed':
        return 'bg-red-900/30 text-red-400 border-red-700';
      default:
        return 'bg-slate-700/50 text-slate-400 border-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <svg
            className="w-5 h-5 text-green-400"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'pending':
        return (
          <svg
            className="w-5 h-5 text-yellow-400"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'processing':
        return (
          <svg
            className="w-5 h-5 text-orange-400 animate-spin"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'failed':
        return (
          <svg
            className="w-5 h-5 text-red-400"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'paypal':
        return 'PayPal';
      case 'bank':
        return 'Bank Transfer';
      case 'check':
        return 'Check';
      case 'crypto':
        return 'Cryptocurrency';
      default:
        return method;
    }
  };

  if (payouts.length === 0) {
    return (
      <div className="bg-slate-800/80 rounded-lg border border-slate-700/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Payout History</h2>
        </div>
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-slate-600 mx-auto mb-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-slate-400">No payout history yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Your payout requests will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/80 rounded-lg border border-slate-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Payout History</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition flex items-center space-x-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
          <Link
            to="/affiliate/payouts/all"
            className="text-sm text-orange-400 hover:text-orange-300"
          >
            View All
          </Link>
        </div>
      </div>

      <div className="space-y-4">
        {payouts.map((payout) => (
          <div
            key={payout.id}
            className="bg-slate-700/50/30 rounded-lg p-4 border border-gray-600 hover:bg-slate-700/50/50 transition"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                {getStatusIcon(payout.status)}
                <div>
                  <p className="text-lg font-bold text-white">
                    ${payout.amount.toFixed(2)}
                  </p>
                  <p className="text-sm text-slate-400">
                    {getPaymentMethodLabel(payout.paymentMethod)} •{' '}
                    {payout.commissionCount} commissions
                  </p>
                </div>
              </div>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(payout.status)}`}
              >
                {payout.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Requested</p>
                <p className="text-white">
                  {new Date(payout.requestedAt).toLocaleDateString()}
                </p>
              </div>
              {payout.completedAt && (
                <div>
                  <p className="text-slate-400">Completed</p>
                  <p className="text-white">
                    {new Date(payout.completedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
              {payout.transactionId && (
                <div className="col-span-2">
                  <p className="text-slate-400">Transaction ID</p>
                  <p className="text-white font-mono text-xs">
                    {payout.transactionId}
                  </p>
                </div>
              )}
              {payout.failureReason && (
                <div className="col-span-2">
                  <p className="text-slate-400">Failure Reason</p>
                  <p className="text-red-400">{payout.failureReason}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
